import { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo, ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase, SUPABASE_URL } from '@/lib/supabase/client';
import { trackSupabaseQueryError } from '@/lib/supabase-tracked';
import { identifyUser, resetUser, trackEvent } from '@/lib/posthog';
import { identityService } from '@/lib/analytics/identity';
import { authEvents } from '@/lib/analytics/events/auth';
import { errorEvents } from '@/lib/analytics/events/errors';
import { criticalPathEvents } from '@/lib/analytics/events';
import { trackCriticalEvent } from '@/lib/analytics/trackCriticalEvent';
import { logger } from '@/lib/logger';
import { gtm } from '@/lib/gtm';
import { googleAdsConversions } from '@/lib/googleAdsConversions';
import { isAdvisorMetadata } from '@/lib/auth/advisorMetadata';
import { getAdvisorToken } from '@/lib/auth/advisorToken';
import { captureError, setUserContext, addBreadcrumb } from '@/lib/errorTracking';
import type { Database } from '@/integrations/supabase/types';

type Profile = Database['public']['Tables']['profiles']['Row'];

/**
 * Fire returned_day_1/7/30 events based on profile.created_at.
 * Each event fires at most once per user (tracked via localStorage).
 */
function checkRetentionEvents(userId: string, createdAt: string | null): void {
  if (!createdAt) return;

  const signupTime = new Date(createdAt).getTime();
  const now = Date.now();
  const daysSinceSignup = (now - signupTime) / (1000 * 60 * 60 * 24);

  const thresholds = [
    { days: 1, key: `sw_returned_day_1_${userId}`, event: criticalPathEvents.returnedDay1 },
    { days: 7, key: `sw_returned_day_7_${userId}`, event: criticalPathEvents.returnedDay7 },
    { days: 30, key: `sw_returned_day_30_${userId}`, event: criticalPathEvents.returnedDay30 },
  ] as const;

  for (const { days, key, event } of thresholds) {
    if (daysSinceSignup >= days && !localStorage.getItem(key)) {
      localStorage.setItem(key, new Date().toISOString());
      trackCriticalEvent(event());
    }
  }
}

/**
 * Ensure user has a Stripe customer ID.
 * Fire-and-forget - errors are logged but don't affect auth flow.
 * Uses 5s timeout to prevent blocking auth.
 */
async function ensureStripeCustomer(accessToken: string, isAdvisor: boolean): Promise<void> {
  const TIMEOUT_MS = 5000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/ensure-stripe-customer`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          account_type: isAdvisor ? 'advisor' : 'user',
        }),
        signal: controller.signal,
      }
    );
    clearTimeout(timeoutId);

    if (response.ok) {
      const result = await response.json();
      if (result.deferred) {
        // Profile not ready yet — retry once after 2s
        logger.log('[Auth] Stripe customer setup deferred, retrying in 2s');
        setTimeout(() => ensureStripeCustomer(accessToken, isAdvisor), 2000);
      } else {
        logger.log('[Auth] Stripe customer ensured:', result.created ? '(new)' : '(existing)');
      }
    } else {
      const error = await response.json().catch(() => ({}));
      logger.error('[Auth] Failed to ensure Stripe customer:', error);

      // Track edge function error for observability (dual tracking)
      const errorEvent = errorEvents.edgeFunctionError(
        'ensure-stripe-customer',
        error.message || `HTTP ${response.status}`
      );
      trackEvent(errorEvent.event, errorEvent.properties);

      // Also capture to Bugsnag for detailed debugging
      captureError(new Error(error.message || `HTTP ${response.status}`), {
        action: 'ensureStripeCustomer',
        metadata: {
          httpStatus: response.status,
          isAdvisor,
          errorCode: error.code,
        },
      });
    }
  } catch (err) {
    clearTimeout(timeoutId);

    // Handle timeout separately - don't report as error, just log
    if (err instanceof Error && err.name === 'AbortError') {
      logger.warn('[Auth] Stripe customer creation timed out - will retry later');
      return;
    }

    // Fire-and-forget - don't let Stripe issues break auth
    logger.error('[Auth] Error ensuring Stripe customer:', err);

    // Track error for observability (dual tracking)
    const errorEvent = errorEvents.edgeFunctionError(
      'ensure-stripe-customer',
      err instanceof Error ? err.message : 'Unknown error'
    );
    trackEvent(errorEvent.event, errorEvent.properties);

    // Also capture to Bugsnag for detailed debugging
    captureError(err, {
      action: 'ensureStripeCustomer',
      metadata: { isAdvisor },
    });
  }
}

/**
 * Send welcome email to new user.
 * Fire-and-forget - errors are logged but don't affect auth flow.
 */
/** Once per browser session per user: when the mail provider is down the profile never records the send and every page load retried (2026-10-01). */
function welcomeEmailAlreadyAttempted(userId: string): boolean {
  try { const k = `sw-welcome-attempted-${userId}`; if (sessionStorage.getItem(k)) return true; sessionStorage.setItem(k, '1'); } catch { /* storage unavailable */ }
  return false;
}

async function sendWelcomeEmail(
  accessToken: string,
  userId: string,
  userEmail: string,
  userName: string | null,
  isAdvisor: boolean
): Promise<void> {
  if (welcomeEmailAlreadyAttempted(userId)) return;
  try {
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/send-welcome-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          userId,
          userEmail,
          userName,
          isAdvisor,
        }),
      }
    );

    // Edge function now always returns 200 for fire-and-forget semantics
    const result = await response.json().catch(() => ({}));

    if (result.success) {
      logger.log('[Auth] Welcome email:', result.messageId ? '(sent)' : '(skipped - already sent)');
    } else if (result.emailSkipped) {
      // Soft failure - email service had an issue but it was handled server-side
      // Don't report to Bugsnag since the edge function already did
      logger.warn('[Auth] Welcome email skipped due to service issue:', result.emailError);
    } else if (result.infrastructureError) {
      // Infrastructure error (DB, parsing, etc.) - already reported server-side
      logger.error('[Auth] Welcome email infrastructure error:', result.details);
    } else if (!response.ok) {
      // Unexpected HTTP error (shouldn't happen with new implementation)
      logger.error('[Auth] Unexpected welcome email response:', response.status);
    }
  } catch (err) {
    // Network error reaching the edge function - fire-and-forget
    logger.error('[Auth] Network error sending welcome email:', err);

    // Only track network errors to the edge function itself
    const errorEvent = errorEvents.edgeFunctionError(
      'send-welcome-email',
      err instanceof Error ? err.message : 'Network error'
    );
    trackEvent(errorEvent.event, errorEvent.properties);

    // Don't capture to Bugsnag for network errors - these are transient
    // and the user can still use the app
  }
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Track last identity update to prevent infinite loops
  const lastIdentityUpdate = useRef<string | null>(null);

  const fetchProfile = async (userId: string, userEmail?: string, userMetadata?: Record<string, unknown>, accessToken?: string): Promise<Profile | null> => {
    logger.log('[Auth] fetchProfile called');

    try {
      const result = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      const data = result.data as Profile | null;
      const error = result.error;

      if (error) {
        logger.error('[Auth] Error fetching profile:', error);
        trackSupabaseQueryError('select', 'profiles', error);

        // Capture to Bugsnag with context
        captureError(error, {
          userId,
          action: 'fetchProfile',
          metadata: {
            operation: 'select',
            table: 'profiles',
            errorCode: error.code,
            errorHint: error.hint,
          },
        });

        return null;
      }

      // If profile doesn't exist, create one
      if (!data && userEmail) {
        // Check user metadata for advisor flag (set during advisor signup)
        const isAdvisor = userMetadata?.is_advisor === true;
        const fullName = typeof userMetadata?.full_name === 'string' ? userMetadata.full_name : null;
        const hasAdvisorToken = !!getAdvisorToken();

        // Phase 5.2: Comprehensive profile creation logging
        logger.log('[Auth] Profile creation starting', {
          userId: userId.substring(0, 8),
          isAdvisor,
          source: isAdvisor ? 'advisor_signup' : 'client_signup',
          hasMetadata: !!userMetadata?.is_advisor,
          hasToken: hasAdvisorToken,
        });

        const profileData: Record<string, unknown> = {
          id: userId,
          email: userEmail,
          state: 'CA',
        };

        // If user signed up as advisor, set appropriate flags
        if (isAdvisor) {
          profileData.is_advisor = true;
          profileData.account_type = 'advisor';
          profileData.onboarding_complete = true; // Advisors skip onboarding
          if (fullName) profileData.full_name = fullName;
        }

        // Set referral source from signup metadata (captured from referral cookie)
        const referralCode = typeof userMetadata?.referral_code === 'string' ? userMetadata.referral_code : null;
        if (referralCode) {
          profileData.referrer = referralCode;
          logger.log('[Auth] Setting referrer on profile:', referralCode);
        }

        const { data: newProfile, error: createError } = await (supabase as any)
          .from('profiles')
          .upsert([profileData], { onConflict: 'id', ignoreDuplicates: false })
          .select()
          .single();

        if (createError || !newProfile) {
          logger.error('[Auth] Error creating profile:', createError);

          if (createError) {
            trackSupabaseQueryError('insert', 'profiles', createError);

            // Capture to Bugsnag with rich context
            captureError(createError, {
              userId,
              action: 'createProfile',
              metadata: {
                operation: 'upsert',
                table: 'profiles',
                isAdvisor,
                errorCode: createError.code,
                errorHint: createError.hint,
                hasFullName: !!fullName,
              },
            });
          }

          // If upsert failed, try to fetch existing profile
          if (createError?.code === '23505' || createError?.code === '409') {
            addBreadcrumb('Profile already exists, fetching existing', {
              userId: userId.substring(0, 8),
            });

            const { data: existingProfile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', userId)
              .single();
            if (existingProfile) {
              return existingProfile as unknown as Profile;
            }
          }
          return null;
        }
        const typedProfile = newProfile as unknown as Profile;

        // Phase 5.2: Track profile creation in PostHog
        logger.log('[Auth] Profile created successfully:', isAdvisor ? '(advisor)' : '(client)');
        trackEvent('profile_created', {
          is_advisor: isAdvisor,
          source: isAdvisor ? 'advisor_signup' : 'client_signup',
          has_metadata_flag: !!userMetadata?.is_advisor,
          has_advisor_token: hasAdvisorToken,
        });

        // Create Stripe customer and send welcome email for new users (fire-and-forget)
        // Use passed accessToken if available, otherwise fall back to getSession()
        let token = accessToken;
        if (!token) {
          const { data: sessionData } = await supabase.auth.getSession();
          token = sessionData.session?.access_token;
          logger.log('[Auth] Session fallback for welcome email:', {
            hasSession: !!sessionData.session,
            hasAccessToken: !!token,
          });
        } else {
          logger.log('[Auth] Using passed access token for welcome email');
        }

        if (token) {
          ensureStripeCustomer(token, isAdvisor);

          // Send welcome email to new users (fire-and-forget)
          logger.log('[Auth] Triggering welcome email for new user');
          sendWelcomeEmail(
            token,
            userId,
            userEmail,
            fullName,
            isAdvisor
          );
        } else {
          // Log when session is missing - this is a bug if it happens
          logger.warn('[Auth] Cannot send welcome email - no access token available', {
            userId: userId.substring(0, 8),
            passedToken: !!accessToken,
          });
          captureError(new Error('Welcome email skipped - no access token'), {
            userId,
            action: 'sendWelcomeEmail',
            metadata: {
              passedToken: !!accessToken,
              isAdvisor,
            },
          });
        }

        return typedProfile;
      }

      logger.log('[Auth] Profile fetched');

      // Phase 3.1: Advisor flag consistency check
      // If user_metadata says advisor but profile doesn't, fix the mismatch
      if (isAdvisorMetadata(userMetadata) && !data.is_advisor) {
        logger.warn('[Auth] Fixing advisor flag mismatch - metadata says advisor but profile says client');
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            is_advisor: true,
            account_type: 'advisor',
            onboarding_complete: true,
          })
          .eq('id', userId);

        if (updateError) {
          logger.error('[Auth] Failed to fix advisor flag mismatch:', updateError);
          trackSupabaseQueryError('update', 'profiles', updateError);

          // Capture to Bugsnag
          captureError(updateError, {
            userId,
            action: 'fixAdvisorFlagMismatch',
            metadata: {
              operation: 'update',
              table: 'profiles',
              hadMetadataFlag: true,
              hadProfileFlag: false,
              errorCode: updateError.code,
            },
          });
        } else {
          // Update local data to reflect the fix
          data.is_advisor = true;
          data.account_type = 'advisor';
          data.onboarding_complete = true;
          logger.log('[Auth] Advisor flag mismatch fixed');

          // Phase 5.2: Track mismatch fix for monitoring
          trackEvent('advisor_flag_mismatch_fixed', {
            user_id_prefix: userId.substring(0, 8),
            had_metadata_flag: true,
            had_profile_flag: false,
          });
        }
      }

      // Ensure existing users have a Stripe customer (lazy backfill)
      // Only if they don't already have one
      if (data && !data.stripe_customer_id) {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session?.access_token) {
          const isAdvisor = data.is_advisor === true;
          ensureStripeCustomer(sessionData.session.access_token, isAdvisor);
        }
      }

      // Send welcome email to users who haven't received one yet (backfill for trigger-created profiles)
      // This handles the case where the database trigger creates the profile before AuthContext runs
      const profileData = data as unknown as { welcome_email_sent_at: string | null };
      if (profileData && !profileData.welcome_email_sent_at && userEmail) {
        // Get access token - prefer passed token, fall back to session
        let token = accessToken;
        if (!token) {
          const { data: sessionData } = await supabase.auth.getSession();
          token = sessionData.session?.access_token;
        }

        if (token) {
          const isAdvisor = data.is_advisor === true;
          const fullName = data.full_name || null;
          logger.log('[Auth] Sending welcome email for trigger-created profile');
          sendWelcomeEmail(token, userId, userEmail, fullName, isAdvisor);
        }
      }

      return data;
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : '';
      const isNetworkError = errMsg === 'Load failed' || errMsg === 'Failed to fetch' || errMsg === 'NetworkError when attempting to fetch resource.';

      if (isNetworkError) {
        logger.warn('[Auth] Network error in fetchProfile — user likely offline');
        return null;
      }

      logger.error('[Auth] Unexpected error in fetchProfile:', err);

      // Track to PostHog
      const errorEvent = errorEvents.supabaseError(
        'select',
        'profiles',
        errMsg || 'Unknown error in fetchProfile',
        'FETCH_PROFILE_ERROR'
      );
      trackEvent(errorEvent.event, errorEvent.properties);

      // Capture to Bugsnag with context
      captureError(err, {
        userId,
        action: 'fetchProfile',
        metadata: {
          hasUserEmail: !!userEmail,
          hasUserMetadata: !!userMetadata,
        },
      });

      return null;
    }
  };

  // Use ref for stable access in callback
  const userRef = useRef(user);
  userRef.current = user;

  const refreshProfile = useCallback(async () => {
    const currentUser = userRef.current;
    if (currentUser) {
      logger.log('[Auth] refreshProfile called');
      try {
        // Add timeout to prevent infinite hang
        const timeoutPromise = new Promise<null>((_, reject) => {
          setTimeout(() => reject(new Error('refreshProfile timeout')), 5000);
        });

        const profileData = await Promise.race([
          fetchProfile(currentUser.id, currentUser.email ?? undefined, currentUser.user_metadata),
          timeoutPromise
        ]);

        logger.log('[Auth] refreshProfile completed');
        setProfile(profileData);
      } catch (err) {
        logger.error('[Auth] refreshProfile error:', err);

        // Track to PostHog
        const errorEvent = errorEvents.supabaseError(
          'select',
          'profiles',
          err instanceof Error ? err.message : 'Unknown error in refreshProfile',
          'REFRESH_PROFILE_ERROR'
        );
        trackEvent(errorEvent.event, errorEvent.properties);

        // Capture to Bugsnag
        captureError(err, {
          userId: currentUser.id,
          action: 'refreshProfile',
          metadata: {
            isTimeout: err instanceof Error && err.message.includes('timeout'),
          },
        });

        // Don't throw - just log the error. The profile might be stale but app should still work.
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Get initial session with timeout and retry
    const initSession = async () => {
      logger.log('[Auth] Starting initSession...');

      const getSessionWithTimeout = (timeoutMs: number) => {
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('getSession timeout')), timeoutMs);
        });
        return Promise.race([supabase.auth.getSession(), timeoutPromise]);
      };

      try {
        let result;
        const timeouts = [8000, 15000, 25000];
        for (let attempt = 0; attempt < timeouts.length; attempt++) {
          try {
            result = await getSessionWithTimeout(timeouts[attempt]);
            break;
          } catch (attemptErr) {
            if (attemptErr instanceof Error && attemptErr.message === 'getSession timeout') {
              logger.log(`[Auth] getSession attempt ${attempt + 1}/${timeouts.length} timed out`);
              addBreadcrumb(`Session timeout attempt ${attempt + 1}`);
              if (attempt === timeouts.length - 1) {
                throw attemptErr;
              }
            } else {
              throw attemptErr;
            }
          }
        }
        if (!result) throw new Error('getSession timeout');

        const { data: { session }, error } = result;
        logger.log('[Auth] getSession returned:', { hasSession: !!session });

        if (!isMounted) return;

        if (error) {
          logger.error('[Auth] Error getting session:', error);

          // Track to PostHog
          const errorEvent = errorEvents.authError('get_session', error.message);
          trackEvent(errorEvent.event, errorEvent.properties);

          // Capture to Bugsnag
          captureError(error, {
            action: 'getSession',
            metadata: {
              errorCode: error.code,
              errorStatus: error.status,
            },
          });
        } else {
          setSession(session);
          setUser(session?.user ?? null);
          logger.log('[Auth] Set user:', session?.user ? 'authenticated' : 'null');

          if (session?.user) {
            logger.log('[Auth] Fetching profile...');

            // Set user context in Bugsnag for all subsequent errors
            setUserContext({
              id: session.user.id,
              email: session.user.email || undefined,
            });

            // Add breadcrumb for session restore
            addBreadcrumb('User session restored', {
              userId: session.user.id.substring(0, 8),
              hasEmail: !!session.user.email,
            });

            // Identify user for analytics with rich properties
            identifyUser(session.user.id, { email: session.user.email });

            // Enhanced identify after profile loads (handled below in fetchProfile callback)

            fetchProfile(session.user.id, session.user.email ?? undefined, session.user.user_metadata, session.access_token).then((p) => {
              logger.log('[Auth] Profile fetched:', p ? 'success' : 'null');
              if (isMounted) {
                setProfile(p);
                // Update identity with profile properties (with dedup to prevent loops)
                if (p) {
                  const profileKey = `${p.id}-${p.updated_at}`;
                  if (lastIdentityUpdate.current !== profileKey) {
                    lastIdentityUpdate.current = profileKey;
                    identityService.updateOnboardingProperties(p);

                    // Enhanced PostHog identify with full profile properties
                    identifyUser(p.id, {
                      email: p.email,
                      name: p.full_name,
                      full_name: p.full_name,
                      plan: (p as unknown as Record<string, unknown>).subscription_tier || 'trial',
                      subscription_plan: (p as unknown as Record<string, unknown>).subscription_tier,
                      user_type: p.is_advisor ? 'advisor' : 'diy',
                      state: p.state,
                      company_name: (p as unknown as Record<string, unknown>).company_name,
                      created_at: p.created_at,
                    });

                    // Update Bugsnag user context with full profile info
                    setUserContext({
                      id: p.id,
                      email: p.email || undefined,
                      name: p.full_name || undefined,
                    });

                    // Check retention events (returned_day_1/7/30)
                    checkRetentionEvents(p.id, p.created_at);
                  }
                }
              }
            });
          }
        }
      } catch (err) {
        logger.error('[Auth] Error in initSession:', err);

        // Track to PostHog
        const errorEvent = errorEvents.authError('init_session', err instanceof Error ? err.message : 'Unknown error');
        trackEvent(errorEvent.event, errorEvent.properties);

        // Capture to Bugsnag
        captureError(err, {
          action: 'initSession',
          metadata: {
            isTimeout: err instanceof Error && err.message === 'getSession timeout',
          },
        });

        // On timeout or error, clear potentially corrupted storage and continue
        if (err instanceof Error && err.message === 'getSession timeout') {
          logger.log('[Auth] Session check timed out, clearing storage...');
          addBreadcrumb('Session timeout, clearing storage');

          if (typeof window !== 'undefined') {
            // Clear both old and new storage keys
            localStorage.removeItem('sb-sqevbwxzsyfzcputaeqz-auth-token');
            localStorage.removeItem('settlewise-auth');
          }
        }
      } finally {
        logger.log('[Auth] Setting loading to false');
        if (isMounted) setLoading(false);
      }
    };

    initSession();

    // Listen for auth changes
    const { data } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Set user context in Bugsnag for all subsequent errors
          setUserContext({
            id: session.user.id,
            email: session.user.email || undefined,
          });

          // Add breadcrumb for auth state change
          addBreadcrumb(`Auth event: ${event}`, {
            userId: session.user.id.substring(0, 8),
            hasEmail: !!session.user.email,
          });

          // Fetch profile in background (pass access token to avoid race condition)
          fetchProfile(session.user.id, session.user.email ?? undefined, session.user.user_metadata, session.access_token).then((p) => {
            if (isMounted) {
              setProfile(p);
              // Update identity with profile properties (with dedup to prevent loops)
              if (p) {
                const profileKey = `${p.id}-${p.updated_at}`;
                if (lastIdentityUpdate.current !== profileKey) {
                  lastIdentityUpdate.current = profileKey;
                  identityService.updateOnboardingProperties(p);

                  // Update Bugsnag user context with full profile info
                  setUserContext({
                    id: p.id,
                    email: p.email || undefined,
                    name: p.full_name || undefined,
                  });
                }
              }
            }
          });
        } else {
          // Clear user context when logged out
          setProfile(null);
          setUserContext(null);
          addBreadcrumb('User logged out');
        }
      }
    );

    const subscription = data?.subscription;

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, [supabase]);

  const signUp = useCallback(async (email: string, password: string) => {
    addBreadcrumb('Sign up initiated', { email: email.replace(/^(.{2}).*(@.*)$/, '$1***$2') });

    // Use current origin for redirect, with env var fallback for SSR edge cases
    // Redirect to dedicated email-confirmed page for better UX
    const baseUrl = typeof window !== 'undefined'
      ? window.location.origin
      : (import.meta.env.VITE_SITE_URL || 'https://www.diiivorce.com');
    const redirectUrl = `${baseUrl}/email-confirmed`;

    // Get UTM attribution data BEFORE signup to include in user metadata
    // This ensures attribution persists through email confirmation flow
    let attributionMetadata: Record<string, string | null> = {};
    try {
      const { getStoredUTMParams } = await import('@/lib/utm');
      const utmParams = getStoredUTMParams();
      if (utmParams) {
        attributionMetadata = {
          utm_source: utmParams.utm_source,
          utm_medium: utmParams.utm_medium,
          utm_campaign: utmParams.utm_campaign,
          utm_content: utmParams.utm_content,
          utm_term: utmParams.utm_term,
          gclid: utmParams.gclid,
          gbraid: utmParams.gbraid,
          wbraid: utmParams.wbraid,
          msclkid: utmParams.msclkid,
          fbclid: utmParams.fbclid,
          referrer: utmParams.referrer,
          landing_page: utmParams.landing_page,
        };
        logger.log('[Auth] Including attribution in signup metadata:', {
          hasGclid: !!utmParams.gclid,
          hasUtmSource: !!utmParams.utm_source,
          landingPage: utmParams.landing_page,
        });
      }
    } catch (err) {
      logger.error('[Auth] Failed to get UTM params for signup:', err);
    }

    // Get referral data from cookie (set by ReferralTracker when user lands with ?ref=CODE)
    try {
      const { getReferralData, clearReferralData } = await import('@/hooks/useReferralTracking');
      const referralData = getReferralData();
      if (referralData) {
        attributionMetadata.referral_code = referralData.referral_code;
        attributionMetadata.referral_landing_page = referralData.landing_page;
        attributionMetadata.referral_cookie_set_at = referralData.cookie_set_at;
        // Merge referral UTM params if not already set by direct UTM tracking
        if (!attributionMetadata.utm_source && referralData.utm_source) {
          attributionMetadata.utm_source = referralData.utm_source;
        }
        if (!attributionMetadata.utm_medium && referralData.utm_medium) {
          attributionMetadata.utm_medium = referralData.utm_medium;
        }
        if (!attributionMetadata.utm_campaign && referralData.utm_campaign) {
          attributionMetadata.utm_campaign = referralData.utm_campaign;
        }
        logger.log('[Auth] Including referral data in signup metadata:', {
          referralCode: referralData.referral_code,
          landingPage: referralData.landing_page,
        });
        // Clear the referral cookie after capturing (attribution saved via user metadata)
        clearReferralData();
        logger.log('[Auth] Referral cookie cleared (saved via user metadata)');
      }
    } catch (err) {
      logger.error('[Auth] Failed to get referral data for signup:', err);
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: attributionMetadata, // Include attribution + referral in user metadata
      },
    });

    if (error) {
      // CRITICAL: Set user context with attempted email BEFORE capturing error
      // This ensures Bugsnag tracks by email instead of IP address for failed sign-up attempts
      setUserContext({
        id: `attempted-signup-${email}`,
        email: email,
        name: undefined,
      });

      // Add breadcrumb for failure
      addBreadcrumb('Sign up failed', {
        errorCode: error.code,
        errorStatus: error.status,
      }, 'error');

      // Track signup failure for analytics
      const event = authEvents.signupFailed(error.message, 'email');
      trackEvent(event.event, event.properties);

      // Track as error for observability (dual tracking)
      const errorEvent = errorEvents.authError('sign_up', error.message, error.code);
      trackEvent(errorEvent.event, errorEvent.properties);

      // Capture to Bugsnag (now with email context set above)
      captureError(error, {
        action: 'signUp',
        metadata: {
          provider: 'email',
          attemptedEmail: email,
          errorCode: error.code,
          errorStatus: error.status,
        },
      });
    } else if (data.user) {
      // Track signup success
      const event = authEvents.signupCompleted('email');
      trackEvent(event.event, event.properties);
      // Agent hub compatible event
      trackEvent('signup_completed', { plan: 'trial', source: 'website' });

      addBreadcrumb('Sign up successful', {
        userId: data.user.id.substring(0, 8),
      });

      // GTM conversion tracking
      gtm.trackSignUp('email');

      // Google Ads conversion tracking
      googleAdsConversions.trackSignUp();

      // Clear UTM params from sessionStorage after signup
      // Attribution data is now saved via user metadata in the signUp call above,
      // and persisted to profile via the database trigger (handle_new_user)
      try {
        const { clearStoredUTMParams } = await import('@/lib/utm');
        clearStoredUTMParams();
        logger.log('[Auth] UTM params cleared from storage (saved via user metadata)');
      } catch (utmErr) {
        logger.error('[Auth] Failed to clear UTM params:', utmErr);
        // Non-critical, don't break signup
      }
    }

    return { error: error as Error | null };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    addBreadcrumb('Sign in initiated', { email: email.replace(/^(.{2}).*(@.*)$/, '$1***$2') });

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      // CRITICAL: Set user context with attempted email BEFORE capturing error
      // This ensures Bugsnag tracks by email instead of IP address for failed sign-in attempts
      setUserContext({
        id: `attempted-signin-${email}`,
        email: email,
        name: undefined,
      });

      // Add breadcrumb for failure
      addBreadcrumb('Sign in failed', {
        errorCode: error.code,
        errorStatus: error.status,
        isInvalidCredentials: error.message.toLowerCase().includes('invalid') ||
                              error.message.toLowerCase().includes('credentials'),
        isEmailNotConfirmed: error.message.toLowerCase().includes('confirm') ||
                             error.message.toLowerCase().includes('verified'),
      }, 'error');

      // Track login failure for analytics
      const event = authEvents.loginFailed(error.message, 'email');
      trackEvent(event.event, event.properties);

      // Track as error for observability (dual tracking)
      const errorEvent = errorEvents.authError('sign_in', error.message, error.code);
      trackEvent(errorEvent.event, errorEvent.properties);

      // Determine severity: expected user flows get lower severity
      const isEmailNotConfirmed = error.message.toLowerCase().includes('confirm') ||
                                  error.message.toLowerCase().includes('verified');
      const isInvalidCredentials = error.message.toLowerCase().includes('invalid') ||
                                   error.message.toLowerCase().includes('credentials');
      const signInSeverity = isEmailNotConfirmed ? 'info' as const
                           : isInvalidCredentials ? 'warning' as const
                           : 'error' as const;

      // Capture to Bugsnag (now with email context set above)
      captureError(error, {
        action: 'signIn',
        severity: signInSeverity,
        metadata: {
          provider: 'email',
          attemptedEmail: email,
          errorCode: error.code,
          errorStatus: error.status,
          isInvalidCredentials,
          isEmailNotConfirmed,
        },
      });
    } else if (data.user) {
      // Track login success
      const event = authEvents.loginCompleted('email');
      trackEvent(event.event, event.properties);

      addBreadcrumb('Sign in successful', {
        userId: data.user.id.substring(0, 8),
      });

      // Set user context for subsequent errors
      setUserContext({
        id: data.user.id,
        email: data.user.email || undefined,
      });

      // GTM conversion tracking
      gtm.trackLogin('email');
    }

    return { error: error as Error | null };
  }, []);

  const signOut = useCallback(async () => {
    addBreadcrumb('Sign out initiated', {
      userId: user?.id?.substring(0, 8),
    });

    // Track logout
    const event = authEvents.logoutCompleted();
    trackEvent(event.event, event.properties);

    await supabase.auth.signOut();

    // Reset analytics identity
    resetUser(); // Reset PostHog user on logout

    // Clear Bugsnag user context
    setUserContext(null);

    addBreadcrumb('Sign out completed');

    setUser(null);
    setSession(null);
    setProfile(null);
  }, [user]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    refreshProfile,
  }), [user, session, profile, loading, signUp, signIn, signOut, refreshProfile]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Safe version of useAuth that returns default values when called outside AuthProvider.
 * Use this in components that may render before the provider is mounted (e.g., during navigation).
 */
export function useAuthSafe(): AuthContextType & { isContextAvailable: boolean } {
  const context = useContext(AuthContext);

  if (context === undefined) {
    // Return safe defaults when context is unavailable
    return {
      user: null,
      session: null,
      profile: null,
      loading: true,
      signUp: async () => ({ error: new Error('AuthProvider not mounted') }),
      signIn: async () => ({ error: new Error('AuthProvider not mounted') }),
      signOut: async () => {},
      refreshProfile: async () => {},
      isContextAvailable: false,
    };
  }

  return { ...context, isContextAvailable: true as const };
}
